import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  User, Globe, MapPin, CreditCard, Plane, History, Contact, Users,
  Briefcase, GraduationCap, Shield, CheckCircle2, ArrowLeft, ArrowRight,
  HelpCircle, X, Check, Clock, AlertCircle, ChevronRight, Minus,
  FileText, Sparkles, Save, Loader2, Home, Bot, Send, MessageCircle, Mic
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import DashboardLayout from "../components/DashboardLayout";
import TrialBlocker from "../components/TrialBlocker";
import VoiceInputButton from "../components/VoiceInputButton";
import { toast } from "sonner";
import { 
  DS160_SECTIONS, 
  COUNTRIES, 
  US_STATES, 
  calculateSectionProgress,
  getSectionQuestionCount 
} from "../config/ds160Config";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Mapeo de nombres de iconos a componentes
const ICON_MAP = {
  User, Globe, MapPin, CreditCard, Plane, History, Contact, Users,
  Briefcase, GraduationCap, Shield, CheckCircle2
};

// Componente del Asistente IA
const AIAssistantPanel = ({ question, section, onClose, formData, onApplySuggestion, profileData }) => {
  const [loading, setLoading] = useState(false);
  const [helpText, setHelpText] = useState(null);
  const [suggestedValue, setSuggestedValue] = useState(null);
  const [suggestionReason, setSuggestionReason] = useState(null);
  const [userQuestion, setUserQuestion] = useState("");
  const [chatMode, setChatMode] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [sendingChat, setSendingChat] = useState(false);
  const chatEndRef = useRef(null);

  useEffect(() => {
    if (question && !chatMode) {
      fetchHelp();
    }
  }, [question]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages]);

  const fetchHelp = async () => {
    setLoading(true);
    setSuggestedValue(null);
    setSuggestionReason(null);
    try {
      const res = await axios.post(`${API}/ds160-assistant/help`, {
        question_id: question.id,
        question_label: question.label,
        section_id: section.id,
        section_name: section.name,
        na_text: question.naText || null,
        profile_data: profileData || null,
        existing_answers: formData || null
      });
      setHelpText(res.data.help_text);
      if (res.data.suggested_value) {
        setSuggestedValue(res.data.suggested_value);
        setSuggestionReason(res.data.suggestion_reason);
      }
    } catch (error) {
      console.error("AI Help error:", error);
      setHelpText("No pude obtener ayuda en este momento. Por favor, intenta de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  const handleApplySuggestion = () => {
    if (suggestedValue && onApplySuggestion) {
      onApplySuggestion(suggestedValue);
      toast.success("Sugerencia aplicada");
    }
  };

  const sendChatMessage = async () => {
    if (!userQuestion.trim()) return;
    
    const newMessage = { role: "user", text: userQuestion };
    setChatMessages(prev => [...prev, newMessage]);
    setUserQuestion("");
    setSendingChat(true);
    
    try {
      const res = await axios.post(`${API}/ds160-assistant/chat`, null, {
        params: {
          section_id: section.id,
          section_name: section.name,
          user_message: userQuestion,
          session_id: `ds160_chat_${section.id}_${Date.now()}`
        }
      });
      setChatMessages(prev => [...prev, { role: "assistant", text: res.data.response }]);
    } catch (error) {
      setChatMessages(prev => [...prev, { 
        role: "assistant", 
        text: "Lo siento, no puedo responder en este momento." 
      }]);
    } finally {
      setSendingChat(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 20 }}
      className="fixed right-4 top-24 w-96 bg-white rounded-2xl shadow-2xl border border-navy-200 z-50 overflow-hidden"
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-4 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
              <Bot className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-bold">Asistente IA</h3>
              <p className="text-xs text-white/80">Te ayudo con el DS-160</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 max-h-96 overflow-y-auto">
        {!chatMode ? (
          // Modo ayuda contextual
          <>
            <div className="mb-4 p-3 bg-indigo-50 rounded-xl">
              <p className="text-sm font-medium text-indigo-900 mb-1">Pregunta actual:</p>
              <p className="text-sm text-indigo-700">{question?.label}</p>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
              </div>
            ) : (
              <div className="space-y-4">
                {/* Smart Suggestion */}
                {suggestedValue && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-4 bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Sparkles className="w-4 h-4 text-emerald-600" />
                      <span className="text-sm font-bold text-emerald-800">Sugerencia inteligente</span>
                    </div>
                    <p className="text-sm text-emerald-900 font-medium mb-2 p-2 bg-white rounded-lg border border-emerald-200">
                      "{suggestedValue}"
                    </p>
                    {suggestionReason && (
                      <p className="text-xs text-emerald-600 mb-3">{suggestionReason}</p>
                    )}
                    <button
                      onClick={handleApplySuggestion}
                      className="w-full flex items-center justify-center gap-2 p-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium"
                    >
                      <Check className="w-4 h-4" />
                      Usar esta sugerencia
                    </button>
                  </motion.div>
                )}

                {/* Help Text */}
                <div className="p-4 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl">
                  <div className="flex items-start gap-3">
                    <Sparkles className="w-5 h-5 text-indigo-600 mt-0.5 shrink-0" />
                    <p className="text-sm text-navy-700 whitespace-pre-wrap">{helpText}</p>
                  </div>
                </div>

                <button
                  onClick={() => {
                    setChatMode(true);
                    setChatMessages([{
                      role: "assistant",
                      text: `¡Hola! Soy tu asistente para el formulario DS-160. Estoy aquí para ayudarte con la sección "${section?.name}". ¿Tienes alguna pregunta específica?`
                    }]);
                  }}
                  className="w-full flex items-center justify-center gap-2 p-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-colors"
                >
                  <MessageCircle className="w-4 h-4" />
                  Tengo más preguntas
                </button>
              </div>
            )}
          </>
        ) : (
          // Modo chat
          <div className="space-y-3">
            {chatMessages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[85%] p-3 rounded-2xl ${
                    msg.role === "user"
                      ? "bg-indigo-600 text-white rounded-br-none"
                      : "bg-navy-100 text-navy-800 rounded-bl-none"
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap">{msg.text}</p>
                </div>
              </div>
            ))}
            {sendingChat && (
              <div className="flex justify-start">
                <div className="bg-navy-100 p-3 rounded-2xl rounded-bl-none">
                  <Loader2 className="w-4 h-4 animate-spin text-navy-500" />
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>
        )}
      </div>

      {/* Chat Input */}
      {chatMode && (
        <div className="p-4 border-t border-navy-100">
          <div className="flex gap-2">
            <Input
              value={userQuestion}
              onChange={(e) => setUserQuestion(e.target.value)}
              placeholder="Escribe tu pregunta..."
              onKeyDown={(e) => e.key === "Enter" && sendChatMessage()}
              className="flex-1"
            />
            <Button
              onClick={sendChatMessage}
              disabled={sendingChat || !userQuestion.trim()}
              className="bg-indigo-600 hover:bg-indigo-700 px-3"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </motion.div>
  );
};

// Componente de Tarjeta de Sección
const SectionCard = ({ section, progress, onClick }) => {
  const Icon = ICON_MAP[section.icon] || FileText;
  
  const getStatus = () => {
    if (progress.isComplete) return "completed";
    if (progress.answered > 0) return "in_progress";
    return "pending";
  };
  
  const status = getStatus();
  
  const statusColors = {
    completed: "border-emerald-300 bg-emerald-50 hover:border-emerald-400",
    in_progress: "border-amber-300 bg-amber-50 hover:border-amber-400",
    pending: "border-navy-200 bg-white hover:border-navy-300"
  };
  
  const statusLabels = {
    completed: "Completada",
    in_progress: "En progreso",
    pending: "Pendiente"
  };

  return (
    <motion.button
      whileHover={{ scale: 1.02, y: -2 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className={`w-full p-4 rounded-2xl border-2 ${statusColors[status]} text-left transition-all hover:shadow-lg`}
      data-testid={`section-card-${section.id}`}
    >
      <div className="flex items-start gap-4">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${
          status === "completed" ? "bg-emerald-500 text-white" :
          status === "in_progress" ? "bg-amber-500 text-white" :
          "bg-navy-100 text-navy-500"
        }`}>
          <Icon className="w-6 h-6" />
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-1">
            <h3 className="font-bold text-navy-900 truncate">{section.name}</h3>
            {status === "completed" && <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" />}
            {status === "in_progress" && <Clock className="w-5 h-5 text-amber-500 shrink-0" />}
            {status === "pending" && <AlertCircle className="w-5 h-5 text-navy-300 shrink-0" />}
          </div>
          <p className="text-sm text-navy-500 mb-3">{section.description}</p>
          
          <div className="flex items-center gap-3">
            <div className="flex-1 h-2 bg-navy-100 rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${progress.percentage}%` }}
                className={`h-full rounded-full ${
                  status === "completed" ? "bg-emerald-500" :
                  status === "in_progress" ? "bg-amber-500" :
                  "bg-navy-300"
                }`}
              />
            </div>
            <span className="text-xs font-medium text-navy-600 whitespace-nowrap">
              {progress.answered}/{progress.total}
            </span>
          </div>
          
          <div className="flex items-center justify-between mt-3">
            <span className={`text-xs font-medium px-2 py-1 rounded-full ${
              status === "completed" ? "bg-emerald-100 text-emerald-700" :
              status === "in_progress" ? "bg-amber-100 text-amber-700" :
              "bg-navy-100 text-navy-600"
            }`}>
              {statusLabels[status]}
            </span>
            <ChevronRight className="w-5 h-5 text-navy-400" />
          </div>
        </div>
      </div>
    </motion.button>
  );
};

// Componente de Pregunta Individual
const QuestionField = ({ question, value, naValue, onAnswer, onNA, formData, onAskAI }) => {
  const [showHelp, setShowHelp] = useState(false);
  
  // Verificar si la pregunta debe mostrarse
  if (question.showIf) {
    const { field, value: condValue, values, notValue } = question.showIf;
    const fieldValue = formData[field];
    if (condValue !== undefined && fieldValue !== condValue) return null;
    if (values !== undefined && !values.includes(fieldValue)) return null;
    if (notValue !== undefined && fieldValue === notValue) return null;
  }
  
  // Check if this field type supports voice input
  const supportsVoice = ['text', 'tel', 'email', 'textarea'].includes(question.type);
  
  const renderField = () => {
    if (naValue) {
      return (
        <div className="p-4 bg-navy-50 rounded-xl text-navy-400 italic">
          No Aplica
        </div>
      );
    }
    
    switch (question.type) {
      case "text":
      case "tel":
      case "email":
        return (
          <div className="relative flex items-center gap-2">
            <Input
              type={question.type}
              value={value || ""}
              onChange={(e) => onAnswer(e.target.value)}
              placeholder={question.placeholder}
              className="text-base py-5 pr-12 flex-1"
            />
            <VoiceInputButton
              onResult={(text) => onAnswer(text)}
              size="sm"
              className="absolute right-2"
            />
          </div>
        );
      
      case "textarea":
        return (
          <div className="relative">
            <Textarea
              value={value || ""}
              onChange={(e) => onAnswer(e.target.value)}
              placeholder={question.placeholder}
              rows={3}
              className="resize-none pr-12"
            />
            <VoiceInputButton
              onResult={(text) => {
                const newValue = value ? `${value} ${text}` : text;
                onAnswer(newValue);
              }}
              size="sm"
              className="absolute right-2 top-2"
            />
          </div>
        );
      
      case "date":
        return (
          <Input
            type="date"
            value={value || ""}
            onChange={(e) => onAnswer(e.target.value)}
            className="text-base py-5"
          />
        );
      
      case "boolean":
        return (
          <div className="flex items-center gap-4 p-4 bg-navy-50 rounded-xl">
            <span className={`text-sm ${!value ? "font-medium text-navy-900" : "text-navy-400"}`}>No</span>
            <Switch checked={value || false} onCheckedChange={onAnswer} />
            <span className={`text-sm ${value ? "font-medium text-navy-900" : "text-navy-400"}`}>Sí</span>
          </div>
        );
      
      case "select":
        return (
          <Select value={value || ""} onValueChange={onAnswer}>
            <SelectTrigger className="text-base py-5">
              <SelectValue placeholder="Seleccionar..." />
            </SelectTrigger>
            <SelectContent>
              {question.options.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      
      case "country":
        return (
          <Select value={value || ""} onValueChange={onAnswer}>
            <SelectTrigger className="text-base py-5">
              <SelectValue placeholder="Seleccionar país..." />
            </SelectTrigger>
            <SelectContent>
              {COUNTRIES.map(country => (
                <SelectItem key={country} value={country}>{country}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      
      case "us_state":
        return (
          <Select value={value || ""} onValueChange={onAnswer}>
            <SelectTrigger className="text-base py-5">
              <SelectValue placeholder="Seleccionar estado..." />
            </SelectTrigger>
            <SelectContent>
              {US_STATES.map(state => (
                <SelectItem key={state} value={state}>{state}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      
      default:
        return (
          <Input
            value={value || ""}
            onChange={(e) => onAnswer(e.target.value)}
            placeholder={question.placeholder}
          />
        );
    }
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl border border-navy-200 p-5 shadow-sm"
    >
      {/* Label */}
      <div className="flex items-start gap-2 mb-3">
        <label className="text-base font-semibold text-navy-900 flex-1">
          {question.label}
          {question.required && !question.allowNA && <span className="text-red-500 ml-1">*</span>}
        </label>
        <div className="flex items-center gap-1 shrink-0">
          {/* Botón Ayuda IA */}
          <button
            type="button"
            onClick={onAskAI}
            className="p-1.5 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white transition-all shadow-sm hover:shadow-md"
            title="Pedir ayuda a la IA"
          >
            <Bot className="w-4 h-4" />
          </button>
          {question.hint && (
            <button
              type="button"
              onClick={() => setShowHelp(!showHelp)}
              className="p-1.5 rounded-full bg-blue-50 hover:bg-blue-100 text-blue-600 transition-colors"
            >
              <HelpCircle className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
      
      {/* Hint */}
      <AnimatePresence>
        {showHelp && question.hint && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-xl"
          >
            <div className="flex items-start gap-2">
              <Sparkles className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />
              <p className="text-sm text-blue-700">{question.hint}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Campo */}
      <div className={naValue ? "opacity-50 pointer-events-none" : ""}>
        {renderField()}
      </div>
      
      {/* Botón No Aplica */}
      {question.allowNA && (
        <div className="mt-3 pt-3 border-t border-navy-100">
          <button
            type="button"
            onClick={() => onNA(!naValue)}
            className={`w-full flex items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all ${
              naValue 
                ? "border-amber-400 bg-amber-50 text-amber-700" 
                : "border-navy-200 bg-navy-50 text-navy-600 hover:border-amber-300 hover:bg-amber-50"
            }`}
          >
            {naValue ? (
              <>
                <Check className="w-4 h-4" />
                <span className="text-sm font-medium">Marcado: {question.naText}</span>
              </>
            ) : (
              <>
                <Minus className="w-4 h-4" />
                <span className="text-sm font-medium">{question.naText}</span>
              </>
            )}
          </button>
        </div>
      )}
    </motion.div>
  );
};

// Vista detalle de una sección
const SectionDetail = ({ section, formData, naFields, onUpdateField, onUpdateNA, onBack, onComplete, saving }) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [showAIAssistant, setShowAIAssistant] = useState(false);
  const progress = calculateSectionProgress(section, formData, naFields);
  
  // Filtrar preguntas visibles
  const visibleQuestions = section.questions.filter(q => {
    if (!q.showIf) return true;
    const { field, value, values, notValue } = q.showIf;
    const fieldValue = formData[field];
    if (value !== undefined) return fieldValue === value;
    if (values !== undefined) return values.includes(fieldValue);
    if (notValue !== undefined) return fieldValue !== notValue;
    return true;
  });
  
  const currentQuestion = visibleQuestions[currentQuestionIndex];
  const Icon = ICON_MAP[section.icon] || FileText;
  
  const goNext = () => {
    if (currentQuestionIndex < visibleQuestions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };
  
  const goPrev = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };
  
  const isQuestionAnswered = (q) => {
    if (naFields[q.id]) return true;
    const val = formData[q.id];
    if (typeof val === 'boolean') return true;
    if (typeof val === 'string') return val.trim() !== '';
    return val !== undefined && val !== null;
  };

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-navy-600 hover:text-navy-900 mb-4 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Volver a secciones
        </button>
        
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-xl flex items-center justify-center bg-gradient-to-br from-blue-500 to-indigo-600 text-white shrink-0">
            <Icon className="w-7 h-7" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-navy-900">{section.name}</h1>
            <p className="text-navy-500">{section.description}</p>
          </div>
        </div>
      </div>
      
      {/* Progreso */}
      <div className="bg-gradient-to-r from-navy-800 to-navy-900 rounded-2xl p-5 mb-6 text-white">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm text-navy-300">Progreso de esta sección</span>
          <div className="flex items-center gap-2">
            {saving && <Loader2 className="w-4 h-4 animate-spin text-amber-400" />}
            <span className="text-2xl font-bold">{progress.percentage}%</span>
          </div>
        </div>
        <div className="h-3 bg-navy-700 rounded-full overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${progress.percentage}%` }}
            className={`h-full rounded-full ${progress.isComplete ? "bg-emerald-500" : "bg-amber-400"}`}
          />
        </div>
        <p className="text-sm text-navy-300 mt-2">
          {progress.answered} de {progress.total} preguntas respondidas
        </p>
      </div>
      
      {/* Navegación por puntos */}
      <div className="flex items-center justify-center gap-1.5 mb-6 flex-wrap">
        {visibleQuestions.map((q, index) => {
          const isAnswered = isQuestionAnswered(q);
          const isCurrent = index === currentQuestionIndex;
          return (
            <button
              key={q.id}
              onClick={() => setCurrentQuestionIndex(index)}
              className={`h-2.5 rounded-full transition-all ${
                isCurrent 
                  ? "w-6 bg-blue-500" 
                  : isAnswered 
                  ? "w-2.5 bg-emerald-500" 
                  : "w-2.5 bg-navy-200 hover:bg-navy-300"
              }`}
              title={q.label}
            />
          );
        })}
      </div>
      
      {/* Indicador de pregunta */}
      <div className="flex items-center justify-between mb-4 text-sm">
        <span className="text-navy-400">
          Pregunta {currentQuestionIndex + 1} de {visibleQuestions.length}
        </span>
        {currentQuestion?.allowNA && (
          <span className="px-2 py-1 bg-blue-50 text-blue-600 rounded-full text-xs">
            Permite "No Aplica"
          </span>
        )}
      </div>
      
      {/* Pregunta actual */}
      {currentQuestion && (
        <QuestionField
          question={currentQuestion}
          value={formData[currentQuestion.id]}
          naValue={naFields[currentQuestion.id]}
          onAnswer={(val) => onUpdateField(currentQuestion.id, val)}
          onNA={(val) => onUpdateNA(currentQuestion.id, val)}
          formData={formData}
          onAskAI={() => setShowAIAssistant(true)}
        />
      )}
      
      {/* AI Assistant Panel */}
      <AnimatePresence>
        {showAIAssistant && currentQuestion && (
          <AIAssistantPanel
            question={currentQuestion}
            section={section}
            onClose={() => setShowAIAssistant(false)}
            formData={formData}
            profileData={null}
            onApplySuggestion={(value) => {
              onUpdateField(currentQuestion.id, value);
              setShowAIAssistant(false);
            }}
          />
        )}
      </AnimatePresence>
      
      {/* Navegación */}
      <div className="flex items-center justify-between mt-6">
        <Button
          variant="outline"
          onClick={goPrev}
          disabled={currentQuestionIndex === 0}
          className="px-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Anterior
        </Button>
        
        {currentQuestionIndex < visibleQuestions.length - 1 ? (
          <Button
            onClick={goNext}
            className="bg-blue-600 hover:bg-blue-700 px-6"
          >
            Siguiente
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        ) : (
          <Button
            onClick={onComplete}
            disabled={!progress.isComplete}
            className={`px-6 ${progress.isComplete ? "bg-emerald-600 hover:bg-emerald-700" : "bg-navy-300"}`}
          >
            <Check className="w-4 h-4 mr-2" />
            Terminé esta sección
          </Button>
        )}
      </div>
      
      {/* Mensaje si faltan preguntas */}
      {currentQuestionIndex === visibleQuestions.length - 1 && !progress.isComplete && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl"
        >
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-amber-500 shrink-0" />
            <p className="text-sm text-amber-700">
              Faltan <strong>{progress.total - progress.answered} pregunta(s)</strong> por responder. 
              Usa "No Aplica" si alguna no te corresponde.
            </p>
          </div>
        </motion.div>
      )}
    </div>
  );
};

// Componente Principal
const DS160Sections = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [caseData, setCaseData] = useState(null);
  const [formData, setFormData] = useState({});
  const [naFields, setNaFields] = useState({});
  const [view, setView] = useState("overview");
  const [selectedSection, setSelectedSection] = useState(null);
  const [completedSections, setCompletedSections] = useState([]);
  const autoSaveRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  // Auto-save con debounce
  useEffect(() => {
    if (!caseData?.id || loading) return;
    
    if (autoSaveRef.current) {
      clearTimeout(autoSaveRef.current);
    }
    
    autoSaveRef.current = setTimeout(() => {
      saveData();
    }, 2000);
    
    return () => {
      if (autoSaveRef.current) {
        clearTimeout(autoSaveRef.current);
      }
    };
  }, [formData, naFields]);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/cases/my-case`);
      setCaseData(res.data);
      if (res.data?.ds160_data) {
        setFormData(res.data.ds160_data);
        setNaFields(res.data.ds160_data.na_fields || {});
        setCompletedSections(res.data.ds160_data.completed_sections || []);
      }
    } catch (error) {
      console.error("Error fetching case:", error);
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  };

  const saveData = async () => {
    if (!caseData?.id) return;
    
    setSaving(true);
    try {
      await axios.post(`${API}/cases/${caseData.id}/ds160`, {
        ...formData,
        na_fields: naFields,
        completed_sections: completedSections
      });
    } catch (error) {
      console.error("Auto-save error:", error);
      // Solo mostrar toast si es un error real del servidor, no de red
      if (error?.response?.status >= 400) {
        toast.error("Error al guardar automáticamente. Intenta guardar manualmente.", { duration: 3000 });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateField = (fieldId, value) => {
    setFormData(prev => ({ ...prev, [fieldId]: value }));
    // Remover de NA si responde
    if (naFields[fieldId]) {
      setNaFields(prev => {
        const next = { ...prev };
        delete next[fieldId];
        return next;
      });
    }
  };

  const handleUpdateNA = (fieldId, isNA) => {
    if (isNA) {
      setNaFields(prev => ({ ...prev, [fieldId]: true }));
      // Limpiar valor del campo
      setFormData(prev => {
        const next = { ...prev };
        delete next[fieldId];
        return next;
      });
    } else {
      setNaFields(prev => {
        const next = { ...prev };
        delete next[fieldId];
        return next;
      });
    }
  };

  const handleSectionClick = (section) => {
    setSelectedSection(section);
    setView("detail");
  };

  const handleBack = () => {
    setView("overview");
    setSelectedSection(null);
  };

  const handleCompleteSection = async () => {
    if (!selectedSection) return;
    
    // Marcar como completada
    const newCompletedSections = completedSections.includes(selectedSection.id) 
      ? completedSections 
      : [...completedSections, selectedSection.id];
    
    if (!completedSections.includes(selectedSection.id)) {
      setCompletedSections(prev => [...prev, selectedSection.id]);
    }
    
    // Guardar y volver
    setSaving(true);
    try {
      await axios.post(`${API}/cases/${caseData.id}/ds160`, {
        ...formData,
        na_fields: naFields,
        completed_sections: newCompletedSections
      });
      toast.success(`Sección "${selectedSection.name}" completada`);
      
      // Check if ALL sections are now complete - notify oficial
      const allSectionsComplete = DS160_SECTIONS.every(section => {
        const progress = calculateSectionProgress(section, formData, naFields);
        return progress.isComplete || newCompletedSections.includes(section.id);
      });
      
      if (allSectionsComplete && caseData?.id) {
        try {
          await axios.post(`${API}/service/notify-ds160-complete`, {
            case_id: caseData.id
          });
          toast.success("¡DS-160 completado! Tu oficial ha sido notificado para agendar tu cita.", {
            duration: 5000,
            icon: "🎉"
          });
        } catch (notifyError) {
          console.error("Error notifying oficial:", notifyError);
        }
      }
    } catch (error) {
      console.error("Error saving DS160:", error);
      const errorMessage = error?.response?.data?.detail || error?.message || "Error desconocido";
      toast.error(`Error al guardar: ${errorMessage}`, { duration: 5000 });
    } finally {
      setSaving(false);
    }
    
    setView("overview");
    setSelectedSection(null);
  };

  // Calcular progreso global
  const calculateOverallProgress = () => {
    let totalAnswered = 0;
    let totalQuestions = 0;
    
    DS160_SECTIONS.forEach(section => {
      const progress = calculateSectionProgress(section, formData, naFields);
      totalAnswered += progress.answered;
      totalQuestions += progress.total;
    });
    
    return {
      answered: totalAnswered,
      total: totalQuestions,
      percentage: totalQuestions > 0 ? Math.round((totalAnswered / totalQuestions) * 100) : 0
    };
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-12 h-12 text-emerald-600 animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  const overallProgress = calculateOverallProgress();
  const completedCount = DS160_SECTIONS.filter(s => {
    const p = calculateSectionProgress(s, formData, naFields);
    return p.isComplete;
  }).length;
  const inProgressCount = DS160_SECTIONS.filter(s => {
    const p = calculateSectionProgress(s, formData, naFields);
    return p.answered > 0 && !p.isComplete;
  }).length;

  return (
    <TrialBlocker feature="Asistente DS-160">
    <DashboardLayout>
      {view === "overview" ? (
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <Button
              variant="ghost"
              onClick={() => navigate("/dashboard")}
              className="mb-4 text-navy-600"
            >
              <Home className="w-4 h-4 mr-2" />
              Volver al Dashboard
            </Button>
            <h1 className="text-3xl font-bold text-navy-900 mb-2">
              Formulario DS-160
            </h1>
            <p className="text-navy-500">
              Completa cada sección en el orden que prefieras. Tu progreso se guarda automáticamente.
            </p>
          </div>
          
          {/* Progreso Global */}
          <div className="bg-gradient-to-r from-navy-800 to-navy-900 rounded-2xl p-6 mb-8 text-white">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold">Progreso Total</h3>
                <p className="text-navy-300 text-sm flex items-center gap-2">
                  Formulario DS-160 - Solicitud de Visa
                  {saving && (
                    <span className="flex items-center gap-1 text-amber-400">
                      <Loader2 className="w-3 h-3 animate-spin" />
                      Guardando...
                    </span>
                  )}
                </p>
              </div>
              <div className="text-right">
                <div className="text-4xl font-bold">{overallProgress.percentage}%</div>
                <p className="text-navy-300 text-sm">{overallProgress.answered}/{overallProgress.total} preguntas</p>
              </div>
            </div>
            
            <div className="h-4 bg-navy-700 rounded-full overflow-hidden mb-4">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${overallProgress.percentage}%` }}
                transition={{ duration: 0.8 }}
                className={`h-full rounded-full ${
                  overallProgress.percentage === 100 ? "bg-emerald-500" : 
                  overallProgress.percentage >= 50 ? "bg-amber-400" : "bg-amber-500"
                }`}
              />
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-navy-700/50 rounded-xl p-3 text-center">
                <div className="text-2xl font-bold text-emerald-400">{completedCount}</div>
                <p className="text-xs text-navy-300">Secciones completas</p>
              </div>
              <div className="bg-navy-700/50 rounded-xl p-3 text-center">
                <div className="text-2xl font-bold text-amber-400">{inProgressCount}</div>
                <p className="text-xs text-navy-300">En progreso</p>
              </div>
              <div className="bg-navy-700/50 rounded-xl p-3 text-center">
                <div className="text-2xl font-bold text-navy-400">
                  {DS160_SECTIONS.length - completedCount - inProgressCount}
                </div>
                <p className="text-xs text-navy-300">Pendientes</p>
              </div>
            </div>
          </div>
          
          {/* Grid de Secciones */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {DS160_SECTIONS.map(section => (
              <SectionCard
                key={section.id}
                section={section}
                progress={calculateSectionProgress(section, formData, naFields)}
                onClick={() => handleSectionClick(section)}
              />
            ))}
          </div>
          
          {/* Info */}
          <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-xl">
            <div className="flex items-start gap-3">
              <FileText className="w-5 h-5 text-blue-500 mt-0.5 shrink-0" />
              <div>
                <h4 className="font-semibold text-blue-900">¿Cómo funciona?</h4>
                <ul className="mt-2 space-y-1 text-sm text-blue-700">
                  <li>• Haz clic en cualquier sección para comenzar a responder</li>
                  <li>• Usa <strong>"No Aplica"</strong> si una pregunta no te corresponde</li>
                  <li>• Marca <strong>"Terminé esta sección"</strong> cuando completes todas las preguntas</li>
                  <li>• Puedes regresar a cualquier sección para modificar tus respuestas</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <SectionDetail
          section={selectedSection}
          formData={formData}
          naFields={naFields}
          onUpdateField={handleUpdateField}
          onUpdateNA={handleUpdateNA}
          onBack={handleBack}
          onComplete={handleCompleteSection}
          saving={saving}
        />
      )}
    </DashboardLayout>
    </TrialBlocker>
  );
};

export default DS160Sections;
